
import { updateWhoVieWedmed } from "../../services/apis/dispense/updatewhoviewedmed";
import {
	getCurrentDateInAppTimezone,
	getCurrentDateTimeInAppTimezone,
	getCurrentDayInAppTimezone,
	getCurrentTimeInAppTimezone,
	formatToPartsInAppTimezone,
} from "../constants/timezone";
import isIgnorableAblyConnectionError from "../../monitoring/isIgnorableAblyConnectionError";

export function getCurrentDay() {
	return getCurrentDayInAppTimezone();
}

export const getPharmacyCurrentTime = () => {
	return getCurrentDateTimeInAppTimezone();
}

/**
 * Same format as getPharmacyCurrentTime but returns { datePart, timePart } for UI that shows date and time separately.
 * Use for video call patient header and anywhere that needs CA-based date/time in two parts.
 */
export const getPharmacyCurrentDateTimeParts = () => {
	const parts = formatToPartsInAppTimezone(new Date(), {
		hour12: true,
		hour: 'numeric',
		minute: 'numeric',
		second: 'numeric',
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
	if (!parts.length) return { datePart: '', timePart: '' };

	const getPart = (type) => parts.find((part) => part.type === type)?.value || '';
	return {
		datePart: `${getPart('month')} ${getPart('day')}, ${getPart('year')}`.trim(),
		timePart: `${getPart('hour')}:${getPart('minute')}:${getPart('second')} ${getPart('dayPeriod')}`.trim(),
	};
};

export const getPharmacyCurrentDate = () => {
	return getCurrentDateInAppTimezone();
};

export const getCurrentTime = () => {
	return getCurrentTimeInAppTimezone();
}

export const getPharmacyOrderDeliveryTime = () => {
	return getCurrentDateInAppTimezone();
}


export const DisplayCurrentDay = (setCurrentDate) => {
	const currentCanadaTime = getCurrentDay();
	setCurrentDate(currentCanadaTime)
}

export function getLocation(setCordinate) {
	if (navigator.geolocation) {
		navigator.geolocation.getCurrentPosition((position) => showPosition(position, setCordinate));
	} else {
	}
}

export function getCurrentLocation() {
	return new Promise((resolve, reject) => {
		if (typeof navigator === 'undefined' || !navigator.geolocation) {
			reject(new Error('Location services are not available.'));
			return;
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				resolve({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude
				});
			},
			(error) => reject(error),
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 0
			}
		);
	});
}

export function refreshCurrentLocation(setCordinate) {
	return getCurrentLocation()
		.then((coordinates) => {
			setCordinate(coordinates);
			return coordinates;
		})
		.catch(() => null);
}

function showPosition(position, setCordinate) {
	setCordinate({
		latitude: position.coords.latitude,
		longitude: position.coords.longitude
	});
}

export const getDefaultPharmacist = (pharmacyId, driverDefaultPharmacist) => {
	return driverDefaultPharmacist?.filter(item => item?.pharmacy_id === pharmacyId);
};

/**
 * Detects if the current browser is iOS Safari
 * @returns {boolean} True if running on iOS Safari
 */
export const isIOSSafari = () => {
	if (typeof window === 'undefined') return false;
	
	const userAgent = window.navigator.userAgent;
	const isIOS = /iPad|iPhone|iPod/.test(userAgent);
	const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS/.test(userAgent);
	
	// Also check for iOS 13+ which reports as MacOS
	const isIOS13Plus = /Macintosh/.test(userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
	
	return (isIOS && isSafari) || isIOS13Plus;
};

// Backward-compatible name for existing callers that only use this during cleanup.
export const isIgnorableAblyConnectionCloseError = isIgnorableAblyConnectionError;

export const safeCloseAblyRealtime = (ably) => {
	const connection = ably?.connection;
	if (!connection) return;

	const state = `${connection.state ?? ''}`.toLowerCase();
	if (state === 'closed' || state === 'closing' || state === 'failed') {
		return;
	}

	try {
		ably.close();
	} catch (error) {
		if (!isIgnorableAblyConnectionCloseError(error)) {
			console.warn('Ably close failed:', error?.message || error);
		}
	}
};

/**
 * Detects if the current browser is iOS (any browser)
 * @returns {boolean} True if running on iOS
 */
export const isIOS = () => {
	if (typeof window === 'undefined') return false;
	
	const userAgent = window.navigator.userAgent;
	const isIOS = /iPad|iPhone|iPod/.test(userAgent);
	const isIOS13Plus = /Macintosh/.test(userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
	
	return isIOS || isIOS13Plus;
};


export const handleToggleRXData = (
	index,
	data,
	openIndex,
	setOpenIndex,
	dispatch,
	setSignatureErrors,
	setShowData,
	RemoveAllPatchesId,
	setPatientSignature,
	setShowComment,
	setShowPatientSignature,
	setShowPharmacistSignature,
	setPharmacistSignature,
	setComment,
	setIsPatientVerified,
	setUnconsumedDoseEntries,
	setUnconsumedDoseErrors,
	setMissedExplanation,
	setMissedExplanationError,
	setPId,
	pharmacyId
) => {

	if (openIndex === index) {
		setOpenIndex(null);
	}

	else {
		setOpenIndex(index);
		setSignatureErrors((prevState) => ({
			...prevState,
			patientSign: null,
			pharmacistID: null,
			pharmacistSign: null,
		}));

		const requestedData = {
			pharmacy_id: pharmacyId,
			patient_id: data?.id
		}
		dispatch(updateWhoVieWedmed(requestedData))
	}

	setShowData(false);
	dispatch(RemoveAllPatchesId());
	setPatientSignature([null]);
	setShowComment(false);
	setShowPatientSignature(false);
	setShowPharmacistSignature(false);
	setPharmacistSignature(null);
	setShowComment(false);
	setComment("");
	setIsPatientVerified(false);
	setUnconsumedDoseEntries({});
	setUnconsumedDoseErrors({});
	setMissedExplanation('');
	setMissedExplanationError('');

	setPId(data?.id);
	setTimeout(() => {
		const nextPatientCard = document.getElementById(`patientCard-${index}`);
		if (nextPatientCard) {
			nextPatientCard.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}, 100);
};

/**
 * Parses minutes_until_can_deliver from API (e.g. "59 M", "59M", "59", or 59) to a number of minutes.
 * @param {string|number} value - Raw value from minutes_until_can_deliver
 * @returns {number|null} Minutes as number, or null if unparseable
 */
export const parseMinutesUntilCanDeliver = (value) => {
	if (value == null || value === '') return null;
	const num = Number(value);
	if (!Number.isNaN(num) && num >= 0) return num;
	const str = String(value).trim();
	const match = str.match(/^(\d+(?:\.\d+)?)\s*[Mm]?\s*$/);
	return match ? Number(match[1]) : null;
};

/**
 * Formats a duration for the "can not deliver until" timer in min sec format.
 * @param {number} totalMinutes - Duration in minutes (e.g. from minutes_until_can_deliver; can be decimal)
 * @returns {string} e.g. "45 min 30 sec" or "0 min 0 sec"
 */
export const formatMinutesSecondsForTimer = (totalMinutes) => {
	if (totalMinutes == null || totalMinutes === '') return '0 min 0 sec';
	const total = Number(totalMinutes);
	if (Number.isNaN(total) || total < 0) return '0 min 0 sec';
	const totalSeconds = Math.floor(total * 60);
	const mins = Math.floor(totalSeconds / 60);
	const secs = totalSeconds % 60;
	return `${mins} min ${secs} sec`;
};

/**
 * Formats a duration in compact form for "Next delivery available in Xm Ys".
 * @param {number} totalMinutes - Duration in minutes (can be decimal)
 * @returns {string} e.g. "58m 3s" or "0m 0s"
 */
export const formatMinutesSecondsCompact = (totalMinutes) => {
	if (totalMinutes == null || totalMinutes === '') return '0m 0s';
	const total = Number(totalMinutes);
	if (Number.isNaN(total) || total < 0) return '0m 0s';
	const totalSeconds = Math.floor(total * 60);
	const mins = Math.floor(totalSeconds / 60);
	const secs = totalSeconds % 60;
	return `${mins}m ${secs}s`;
};
